import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface MapCoordinates {
  lat: number;
  lng: number;
}

interface MapLocationPickerProps {
  value: MapCoordinates | null;
  onChange: (coords: MapCoordinates) => void;
  center?: MapCoordinates | null;
  label?: string;
  className?: string;
}

const pickerIcon = L.divIcon({
  className: '',
  html: '<div style="width:16px;height:16px;border-radius:50%;background:#2563eb;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const DEFAULT_CENTER: [number, number] = [9.6412, -13.5784];

export function MapLocationPicker({
  value,
  onChange,
  center,
  label = 'Position sur la carte',
  className = '',
}: MapLocationPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const initial = value ?? center ?? { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] };
    const map = L.map(containerRef.current, {
      center: [initial.lat, initial.lng],
      zoom: 14,
      scrollWheelZoom: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    const marker = L.marker([initial.lat, initial.lng], { icon: pickerIcon, draggable: true }).addTo(map);
    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      onChange({ lat: pos.lat, lng: pos.lng });
    });

    map.on('click', (e) => {
      marker.setLatLng(e.latlng);
      onChange({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    mapRef.current = map;
    markerRef.current = marker;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!markerRef.current || !mapRef.current) return;
    const coords = value ?? center;
    if (!coords) return;
    markerRef.current.setLatLng([coords.lat, coords.lng]);
    mapRef.current.setView([coords.lat, coords.lng], mapRef.current.getZoom());
  }, [value, center]);

  return (
    <div className={className}>
      <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
      <p className="text-xs text-gray-500 mb-2">Cliquez sur la carte ou déplacez le marqueur pour affiner la position.</p>
      <div ref={containerRef} className="w-full h-56 rounded-lg border border-gray-200 z-0" />
      {value && (
        <p className="text-xs text-gray-500 mt-2">
          Position : {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
        </p>
      )}
    </div>
  );
}
